<template>
  <div class="promociones-v2">
    <!-- Top bar -->
    <div class="d-flex align-center px-4 py-2">
      <v-btn small @click="$router.back()" color="info" tile outlined>
        <v-icon left>mdi-arrow-left-bold</v-icon> Volver Atras
      </v-btn>
    </div>

    <v-alert v-if="apiError" type="warning" dense text class="mx-4 mb-0">
      Sin conexión a la API — mostrando datos de ejemplo.
    </v-alert>
    <v-progress-linear v-if="loading" indeterminate />

    <div class="layout-wrapper">

      <!-- ── LEFT: Calendar ── -->
      <div class="cal-section">
        <!-- Month navigation -->
        <div class="d-flex align-center mb-2">
          <v-btn icon small @click="prevMonth"><v-icon>mdi-chevron-left</v-icon></v-btn>
          <span class="text-h6 font-weight-bold mx-2">{{ mesLabel }}</span>
          <v-btn icon small @click="nextMonth"><v-icon>mdi-chevron-right</v-icon></v-btn>
          <v-spacer />
          <v-btn x-small text color="primary" @click="irHoy">Hoy</v-btn>
        </div>

        <!-- Calendar grid -->
        <div class="cal-grid elevation-1">
          <!-- Day headers -->
          <div v-for="d in diasSemana" :key="d" class="cal-header">{{ d }}</div>

          <!-- Empty offset cells -->
          <div v-for="n in primerDiaSemana" :key="'e' + n" class="cal-cell cal-empty"></div>

          <!-- Day cells -->
          <div
            v-for="dia in diasDelMes"
            :key="'d' + dia"
            class="cal-cell"
            :class="{ 'cal-today': esHoy(dia), 'cal-selected': diaSeleccionado === dia }"
            @click="verDia(dia)"
          >
            <!-- Day number -->
            <div class="cal-day-row">
              <span class="cal-day-num" :class="{ 'cal-day-today': esHoy(dia) }">{{ dia }}</span>
              <span v-if="promocionesDelDia(dia).length > 0" class="cal-day-count caption">
                {{ promocionesDelDia(dia).length }}
              </span>
            </div>

            <!-- Max 4 events -->
            <div
              v-for="promo in promocionesDelDia(dia).slice(0, 4)"
              :key="promo.id"
              class="cal-event"
              :class="'cal-event--' + getStatus(promo)"
              :title="promo.nombre"
            >
              <span class="cal-event-dot"></span>
              <span class="cal-event-name">{{ promo.nombre }}</span>
            </div>

            <!-- "+N más" -->
            <div v-if="promocionesDelDia(dia).length > 4" class="cal-more">
              +{{ promocionesDelDia(dia).length - 4 }} más
            </div>

            <!-- Status bars at bottom -->
            <div class="cal-bars">
              <div v-if="contarStatus(dia, 'vigente') > 0"    class="cal-bar cal-bar--vigente"   :style="{ flex: contarStatus(dia, 'vigente') }"></div>
              <div v-if="contarStatus(dia, 'pendiente') > 0"  class="cal-bar cal-bar--pendiente" :style="{ flex: contarStatus(dia, 'pendiente') }"></div>
              <div v-if="contarStatus(dia, 'expirado') > 0"   class="cal-bar cal-bar--expirado"  :style="{ flex: contarStatus(dia, 'expirado') }"></div>
            </div>
          </div>
        </div>

        <!-- Status legend (below calendar) -->
        <div class="d-flex align-center mt-3 px-1" style="gap:16px;">
          <div class="d-flex align-center" style="gap:5px;">
            <span class="legend-dot legend-dot--vigente"></span>
            <span class="caption grey--text text--darken-1">Vigente (aprobado + en rango)</span>
          </div>
          <div class="d-flex align-center" style="gap:5px;">
            <span class="legend-dot legend-dot--pendiente"></span>
            <span class="caption grey--text text--darken-1">Pendiente (sin aprobar)</span>
          </div>
          <div class="d-flex align-center" style="gap:5px;">
            <span class="legend-dot legend-dot--expirado"></span>
            <span class="caption grey--text text--darken-1">Expirado</span>
          </div>
        </div>
      </div>

      <!-- ── RIGHT: Sidebar ── -->
      <div class="sidebar-panel">

        <!-- Header -->
        <div class="sidebar-header">
          <span class="subtitle-1 font-weight-bold">Campañas del mes</span>
          <v-btn
            small color="primary" depressed @click="openCreate"
            v-if="$store.state.CARGO.id == 1 || $store.state.CARGO.id == 14"
          >
            <v-icon left small>mdi-plus</v-icon> Nueva
          </v-btn>
        </div>

        <v-divider />

        <!-- Stats -->
        <div class="sidebar-stats">
          <div class="sidebar-stat-col">
            <div class="text-h6 font-weight-bold">{{ stats.total }}</div>
            <div class="caption grey--text">Total</div>
          </div>
          <v-divider vertical />
          <div class="sidebar-stat-col">
            <div class="text-h6 font-weight-bold green--text text--darken-2">{{ stats.vigentes }}</div>
            <div class="caption grey--text">Vigentes</div>
          </div>
          <v-divider vertical />
          <div class="sidebar-stat-col">
            <div class="text-h6 font-weight-bold orange--text text--darken-2">{{ stats.pendientes }}</div>
            <div class="caption grey--text">Pendientes</div>
          </div>
          <v-divider vertical />
          <div class="sidebar-stat-col">
            <div class="text-h6 font-weight-bold grey--text">{{ stats.expirados }}</div>
            <div class="caption grey--text">Expiradas</div>
          </div>
        </div>

        <v-divider />

        <!-- Tabs bar + contenido scrollable -->
        <div class="sidebar-scroll">
        <div class="tab-bar">
          <button class="tab-btn" :class="{ 'tab-btn--active': tab === 0 }" @click="tab = 0">
            <v-icon small class="mr-1">mdi-format-list-bulleted</v-icon> Leyenda
          </button>
          <button class="tab-btn" :class="{ 'tab-btn--active': tab === 1 }" @click="tab = 1">
            <v-icon small class="mr-1">mdi-calendar-today</v-icon> Ver día
          </button>
        </div>
        <v-divider />

          <!-- ─ Panel 0: Leyenda ─ -->
          <div v-show="tab === 0">
            <div v-if="promocionesDelMes.length === 0" class="pa-6 text-center">
              <v-icon large color="grey lighten-1">mdi-tag-off-outline</v-icon>
              <div class="caption grey--text mt-2">Sin campañas para este mes.</div>
            </div>

            <div v-for="grupo in gruposStatus" :key="grupo.status">
              <v-subheader
                class="px-3 caption font-weight-bold"
                :class="grupo.colorClass"
                style="height:32px;"
              >
                <v-icon x-small left :color="grupo.iconColor">mdi-circle</v-icon>
                {{ grupo.label.toUpperCase() }}
                <v-spacer />
                <v-chip x-small color="grey lighten-3" class="font-weight-bold">{{ grupo.promos.length }}</v-chip>
              </v-subheader>

              <v-list dense class="py-0">
                <v-list-item
                  v-for="promo in grupo.promos"
                  :key="promo.id"
                  class="promo-list-item"
                  :class="'promo-list-item--' + grupo.status"
                >
                  <v-list-item-content class="py-2">
                    <v-list-item-title class="caption font-weight-bold" style="white-space:normal;line-height:1.3;">
                      {{ promo.nombre }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="caption mt-1">
                      <v-icon x-small class="mr-1">mdi-calendar-range</v-icon>
                      {{ formatRangoFecha(promo) }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-if="promo.aprobado_por" class="caption mt-1 green--text text--darken-2">
                      <v-icon x-small color="green darken-2">mdi-check-circle</v-icon>
                      {{ promo.aprobado_por }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action class="my-0 ml-1 align-self-center">
                    <v-chip x-small :color="grupo.chipColor" :dark="grupo.chipDark">
                      {{ grupo.chipLabel }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <v-divider />
            </div>
          </div>

          <!-- ─ Panel 1: Ver día ─ -->
          <div v-show="tab === 1">
            <div v-if="!diaSeleccionado" class="pa-8 text-center">
              <v-icon x-large color="grey lighten-2">mdi-cursor-default-click-outline</v-icon>
              <div class="body-2 grey--text mt-3">Haz clic en un día del calendario</div>
            </div>
            <template v-if="diaSeleccionado">
              <v-sheet color="primary" dark class="pa-3 d-flex align-center">
                <v-icon small class="mr-2">mdi-calendar</v-icon>
                <span class="subtitle-2 font-weight-bold">{{ diaLabelSeleccionado }}</span>
                <v-spacer />
                <v-chip small color="white" light>{{ promosDelDiaSeleccionado.length }}</v-chip>
              </v-sheet>

              <div v-if="promosDelDiaSeleccionado.length === 0" class="pa-6 text-center">
                <v-icon large color="grey lighten-2">mdi-tag-off-outline</v-icon>
                <div class="caption grey--text mt-2">Sin promociones este día.</div>
              </div>

              <v-list dense class="py-0">
                <v-list-item
                  v-for="promo in promosDelDiaSeleccionado"
                  :key="promo.id"
                  class="promo-list-item"
                  :class="'promo-list-item--' + getStatus(promo)"
                >
                  <v-list-item-content class="py-2">
                    <v-list-item-title class="caption font-weight-bold" style="white-space:normal;line-height:1.3;">
                      {{ promo.nombre }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="caption mt-1">
                      <v-icon x-small class="mr-1">mdi-calendar-range</v-icon>
                      {{ formatRangoFecha(promo) }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-if="promo.aprobado_por" class="caption mt-1 green--text text--darken-2">
                      <v-icon x-small color="green darken-2">mdi-check-circle</v-icon>
                      {{ promo.aprobado_por }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action class="my-0 ml-1 align-self-center">
                    <v-chip x-small :color="statusConfig[getStatus(promo)].chipColor" :dark="statusConfig[getStatus(promo)].chipDark">
                      {{ statusConfig[getStatus(promo)].label }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </template>
          </div>

        </div><!-- /sidebar-scroll -->

      </div><!-- /sidebar-panel -->

    </div>

    <CreateV2 ref="createv2modal" v-on:onnew="onnew" />
  </div>
</template>

<script>
import CreateV2 from '@/views/promociones/promo-create-v2.vue';
import xaxios from '@/xaxios';

export default {
  name: 'PromocionesV2',
  components: { CreateV2 },
  data() {
    return {
      loading: false,
      apiError: false,
      tab: 0,
      currentDate: new Date(),
      diaSeleccionado: null,
      promociones: [],
      diasSemana: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
      statusConfig: {
        vigente:  { label: 'Vigente',   chipColor: 'green darken-1',  chipDark: true,  colorClass: 'green--text text--darken-3',  iconColor: 'green darken-1' },
        pendiente:{ label: 'Pendiente', chipColor: 'orange darken-1', chipDark: true,  colorClass: 'orange--text text--darken-3', iconColor: 'orange darken-1' },
        expirado: { label: 'Expirado',  chipColor: 'grey lighten-1',  chipDark: false, colorClass: 'grey--text',                  iconColor: 'grey' },
      },
    };
  },
  computed: {
    mesLabel() {
      const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio',
        'Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      return `${meses[this.currentDate.getMonth()]} ${this.currentDate.getFullYear()}`;
    },
    diasDelMes() {
      const { y, m } = this.ym;
      return Array.from({ length: new Date(y, m + 1, 0).getDate() }, (_, i) => i + 1);
    },
    primerDiaSemana() {
      const { y, m } = this.ym;
      return (new Date(y, m, 1).getDay() + 6) % 7; // Lun = 0
    },
    ym() {
      return { y: this.currentDate.getFullYear(), m: this.currentDate.getMonth() };
    },
    primerDia() {
      const { y, m } = this.ym;
      return new Date(y, m, 1);
    },
    ultimoDia() {
      const { y, m } = this.ym;
      return new Date(y, m + 1, 0);
    },
    promocionesDelMes() {
      return this.promociones.filter(p => {
        const rangos = this.getRangos(p);
        return rangos.some(r => r && r.fin >= this.primerDia && r.inicio <= this.ultimoDia);
      });
    },
    gruposStatus() {
      const grupos = { vigente: [], pendiente: [], expirado: [] };
      this.promocionesDelMes.forEach(p => { grupos[this.getStatus(p)].push(p); });
      return [
        { status: 'vigente',   label: 'Vigentes',   colorClass: 'green--text text--darken-3',  iconColor: 'green darken-1',  chipColor: 'green darken-1',  chipDark: true,  chipLabel: 'Vigente',   promos: grupos.vigente },
        { status: 'pendiente', label: 'Pendientes', colorClass: 'orange--text text--darken-3', iconColor: 'orange darken-1', chipColor: 'orange darken-1', chipDark: true,  chipLabel: 'Pendiente', promos: grupos.pendiente },
        { status: 'expirado',  label: 'Expiradas',  colorClass: 'grey--text',                  iconColor: 'grey',            chipColor: 'grey lighten-1',  chipDark: false, chipLabel: 'Expirado',  promos: grupos.expirado },
      ].filter(g => g.promos.length > 0);
    },
    stats() {
      const v = this.promocionesDelMes.filter(p => this.getStatus(p) === 'vigente').length;
      const p = this.promocionesDelMes.filter(p => this.getStatus(p) === 'pendiente').length;
      const e = this.promocionesDelMes.filter(p => this.getStatus(p) === 'expirado').length;
      return { total: this.promocionesDelMes.length, vigentes: v, pendientes: p, expirados: e };
    },
    promosDelDiaSeleccionado() {
      if (!this.diaSeleccionado) return [];
      return this.promocionesDelDia(this.diaSeleccionado);
    },
    diaLabelSeleccionado() {
      if (!this.diaSeleccionado) return '';
      const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio',
        'Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      const { y, m } = this.ym;
      return `${this.diaSeleccionado} de ${meses[m]} ${y}`;
    },
  },
  methods: {
    getRangos(promo) {
      if (promo.fechas && promo.fechas.length > 0) {
        return promo.fechas
          .filter(f => f.fecha_inicio && f.fecha_fin)
          .map(f => ({ inicio: this.parseDate(f.fecha_inicio), fin: this.parseDate(f.fecha_fin) }));
      }
      if (promo.fecha_inicio && promo.fecha_fin) {
        return [{ inicio: this.parseDate(promo.fecha_inicio), fin: this.parseDate(promo.fecha_fin) }];
      }
      return [];
    },
    parseDate(str) {
      if (!str) return null;
      const [y, m, d] = String(str).split('-').map(Number);
      return new Date(y, m - 1, d);
    },
    // ─ Status: vigente | pendiente | expirado ─────────────────
    getStatus(promo) {
      const aprobado = !!promo.aprobado_por;
      if (!aprobado) return 'pendiente';
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const rangos = this.getRangos(promo);
      const vigente = rangos.some(r => r && today >= r.inicio && today <= r.fin);
      return vigente ? 'vigente' : 'expirado';
    },
    // ─────────────────────────────────────────────────────────
    promocionesDelDia(dia) {
      const { y, m } = this.ym;
      const date = new Date(y, m, dia);
      return this.promocionesDelMes.filter(p =>
        this.getRangos(p).some(r => r && date >= r.inicio && date <= r.fin)
      );
    },
    contarStatus(dia, status) {
      return this.promocionesDelDia(dia).filter(p => this.getStatus(p) === status).length;
    },
    esHoy(dia) {
      const t = new Date();
      const { y, m } = this.ym;
      return t.getFullYear() === y && t.getMonth() === m && t.getDate() === dia;
    },
    formatRangoFecha(promo) {
      const rangos = this.getRangos(promo);
      if (!rangos.length) return '—';
      const r = rangos[0];
      if (!r) return '—';
      if (r.inicio <= this.primerDia && r.fin >= this.ultimoDia) return 'Todo el mes';
      const dS = r.inicio < this.primerDia ? 1 : r.inicio.getDate();
      const dE = r.fin > this.ultimoDia ? this.ultimoDia.getDate() : r.fin.getDate();
      return `Días ${dS}–${dE}`;
    },
    verDia(dia) {
      this.diaSeleccionado = dia;
      this.tab = 1;
    },
    prevMonth() {
      const { y, m } = this.ym;
      this.currentDate = new Date(y, m - 1, 1);
      this.diaSeleccionado = null;
    },
    nextMonth() {
      const { y, m } = this.ym;
      this.currentDate = new Date(y, m + 1, 1);
      this.diaSeleccionado = null;
    },
    irHoy() {
      this.currentDate = new Date();
      this.diaSeleccionado = null;
    },
    openCreate() {
      this.$refs.createv2modal.openmodal();
    },
    onnew() {
      this.getpromos();
    },
    async getpromos() {
      this.loading = true;
      this.apiError = false;
      try {
        const config = { method: 'get', url: `${process.env.VUE_APP_API_REST}/api/v1/esam/promociones` };
        const response = await xaxios.request(config);
        this.promociones = (response.data.data || []).reverse();
      } catch (e) {
        this.apiError = true;
        this.promociones = [];
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.getpromos();
  },
};
</script>

<style scoped>
/* ── Wrapper ─────────────────────────────────── */
.promociones-v2 {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}
.layout-wrapper {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0 12px 12px;
  gap: 12px;
}

/* ── Calendar section ────────────────────────── */
.cal-section {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Grid ────────────────────────────────────── */
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border-radius: 6px;
  overflow: hidden;
  flex: 1;
  align-content: start;
}
.cal-header {
  background: #f5f5f5;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #616161;
  padding: 8px 4px;
  border-bottom: 2px solid #e0e0e0;
}
.cal-cell {
  border-right: 1px solid #eeeeee;
  border-bottom: 1px solid #eeeeee;
  padding: 4px 5px 5px;
  cursor: pointer;
  background: white;
  transition: background 0.1s;
  display: flex;
  flex-direction: column;
  min-height: 120px;
}
.cal-cell:hover   { background: #fafafa; }
.cal-empty        { background: #f9f9f9; cursor: default; min-height: 120px; }
.cal-today        { background: #fffde7; }
.cal-selected     { background: #e8eaf6 !important; }

/* ── Day number row ──────────────────────────── */
.cal-day-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3px;
}
.cal-day-num {
  font-size: 12px;
  font-weight: 500;
  color: #424242;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}
.cal-day-today {
  background: #3f51b5;
  color: white !important;
  font-weight: 700;
}
.cal-day-count {
  font-size: 10px;
  color: #bdbdbd;
  line-height: 1;
}

/* ── Events ──────────────────────────────────── */
.cal-event {
  display: flex;
  align-items: center;
  font-size: 11px;
  padding: 2px 5px;
  border-radius: 3px;
  margin-bottom: 2px;
  overflow: hidden;
  cursor: pointer;
  gap: 4px;
}
.cal-event-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.cal-event-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
  line-height: 1.4;
}

/* Estado: vigente */
.cal-event--vigente            { background: #E8F5E9; color: #1B5E20; }
.cal-event--vigente .cal-event-dot { background: #4CAF50; }

/* Estado: pendiente (sin aprobar) */
.cal-event--pendiente            { background: #FFF3E0; color: #BF360C; }
.cal-event--pendiente .cal-event-dot { background: #FF9800; }

/* Estado: expirado */
.cal-event--expirado            { background: #F5F5F5; color: #757575; }
.cal-event--expirado .cal-event-dot { background: #9E9E9E; }

.cal-more {
  font-size: 10px;
  color: #9e9e9e;
  padding-left: 3px;
  margin-top: 1px;
}

/* ── Status bars ─────────────────────────────── */
.cal-bars {
  display: flex;
  gap: 2px;
  margin-top: auto;
  padding-top: 4px;
}
.cal-bar          { height: 4px; border-radius: 2px; min-width: 4px; }
.cal-bar--vigente  { background: #4CAF50; }
.cal-bar--pendiente{ background: #FF9800; }
.cal-bar--expirado { background: #BDBDBD; }

/* ── Bottom legend ───────────────────────────── */
.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.legend-dot--vigente  { background: #4CAF50; }
.legend-dot--pendiente{ background: #FF9800; }
.legend-dot--expirado { background: #9E9E9E; }

/* ── Sidebar ─────────────────────────────────── */
.sidebar-panel {
  width: 320px;
  min-width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  background: #fff;
}
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  flex-shrink: 0;
}
.sidebar-stats {
  display: flex;
  align-items: center;
  text-align: center;
  flex-shrink: 0;
  padding: 8px 0;
}
.sidebar-stat-col {
  flex: 1;
}
.sidebar-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* ── Tab bar custom ──────────────────────────── */
.tab-bar {
  display: flex;
  background: #f5f5f5;
  flex-shrink: 0;
}
.tab-btn {
  flex: 1;
  height: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #616161;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-bottom: 2px solid transparent;
  transition: color 0.2s, border-color 0.2s;
}
.tab-btn:hover { background: #eeeeee; }
.tab-btn--active {
  color: #1976d2;
  border-bottom-color: #1976d2;
  background: #fff;
}

/* ── Promo list items (v-list) ───────────────── */
.promo-list-item { border-left: 3px solid transparent; }
.promo-list-item--vigente  { border-left-color: #388E3C; background: #F9FBE7; }
.promo-list-item--pendiente{ border-left-color: #F57C00; background: #FFF8F0; }
.promo-list-item--expirado { border-left-color: #BDBDBD; background: #FAFAFA; }
</style>
